import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, startWith, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'app-laft-company-pep-form',
  templateUrl: './laft-company-pep-form.component.html',
  styleUrls: ['./laft-company-pep-form.component.scss'],
})
export class LaftCompanyPepFormComponent implements OnInit {
  @Output() nextStep = new EventEmitter();
  @Output() formChangesEvent = new EventEmitter();
  @Input() initData: any;
  @Input() firstLegalRepresentative: string;
  @Input() legalRepresentativesList: { value: string; text: string }[] = [];
  @Input() isPendingPepFlow: boolean = false;
  @Input() airtableRepresentativesList: {
    value: string;
    text: string;
    documentNumber: string;
    names: string;
    lastNames: string;
  }[] = [];

  formSubmitSubject$ = new Subject();

  get currentRepresentativesList(): { value: string; text: string }[] {
    if (this.isPendingPepFlow) {
      return this.airtableRepresentativesList;
    }
    return this.legalRepresentativesList;
  }

  public pepDataForm: FormGroup;

  constructor(private formBuilder: FormBuilder) {}

  /**
   * Determines if there's only one representative in pendingPep flow.
   * Used to auto-select and potentially hide add buttons.
   */
  get hasSingleAirtableRepresentative(): boolean {
    return this.isPendingPepFlow && this.airtableRepresentativesList.length === 1;
  }

 
  get airtableRepNamesForQuestion(): string {
    if (!this.isPendingPepFlow || !this.airtableRepresentativesList?.length) return '';
    return this.airtableRepresentativesList
      .map(r => {
        const names = (r?.names || '').trim();
        return names.split(/\s+/)[0] || names || r?.text?.split(/\s+/)[0] || '';
      })
      .filter(Boolean)
      .join(' o ');
  }

  get pepQuestion1(): string {
    if (this.airtableRepNamesForQuestion) {
      const names = this.airtableRepNamesForQuestion;
      return this.airtableRepresentativesList.length === 1
        ? `¿${names} es o ha sido PEP?`
        : `¿${names} son o han sido PEP?`;
    }
    return '¿Los representante legales y/o accionistas mencionados anteriormente son o han sido PEP?';
  }

  get pepQuestion2(): string {
    if (this.airtableRepNamesForQuestion) {
      const names = this.airtableRepNamesForQuestion;
      return this.airtableRepresentativesList.length === 1
        ? `¿${names} tiene familiares que son o han sido PEP?`
        : `¿${names} tienen familiares que son o han sido PEP?`;
    }
    return '¿Los representante legales y/o accionistas mencionados anteriormente tienen familiares que son o han sido PEP?';
  }

  ngOnInit(): void {
    this.pepDataForm = this.formBuilder.group({
      isPEP: [false, [Validators.required]],
      pepList: this.formBuilder.array([]),

      hasPEPRelatives: [false, [Validators.required]],
      familyPepList: this.formBuilder.array([]),
    });

    this.pepDataForm.get('isPEP')?.valueChanges.subscribe(isPep => {
      if (isPep) {
        this.addPep();
      } else {
        this.pepList.clear();
        this.pepDataForm.updateValueAndValidity();
      }
    });

    this.pepDataForm.get('hasPEPRelatives')?.valueChanges.subscribe(hasFamilyPep => {
      if (hasFamilyPep) {
        this.addFamilyPep();
      } else {
        this.familyPepList.clear();
        this.pepDataForm.updateValueAndValidity();
      }
    });

    this.pepDataForm.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => {
      this.formChangesEvent.emit(this.buildFormPayload());
    });

    this.formSubmitSubject$
      .pipe(
        tap(() => {
          this.pepDataForm.markAsDirty();
          this.pepDataForm.markAllAsTouched();
          this.pepDataForm.updateValueAndValidity();

          if (this.isPEP) {
            this.pepList.controls.forEach((formGroup: FormGroup) => {
              Object.values(formGroup.controls).forEach(control => {
                control.markAsDirty();
                control.markAllAsTouched();
                control.updateValueAndValidity();

                if (control instanceof FormArray) {
                  Object.values(control.controls).forEach(control => {
                    control.markAsDirty();
                    control.markAllAsTouched();
                    control.updateValueAndValidity();
                  });
                }
              });
            });
          } else {
            this.pepList.clear();
          }

          if (this.hasPEPRelatives) {
            this.familyPepList.controls.forEach((formGroup: FormGroup) => {
              Object.values(formGroup.controls).forEach(control => {
                control.markAsDirty();
                control.markAllAsTouched();
                control.updateValueAndValidity();

                if (control instanceof FormArray) {
                  Object.values(control.controls).forEach(control => {
                    control.markAsDirty();
                    control.markAllAsTouched();
                    control.updateValueAndValidity();
                  });
                }
              });
            });
          } else {
            this.familyPepList.clear();
          }
        }),
        switchMap(() =>
          this.pepDataForm.statusChanges.pipe(
            startWith(this.pepDataForm.status),
            filter(status => status !== 'PENDING'),
            take(1)
          )
        ),
        filter(status => status === 'VALID')
      )
      .subscribe(() => {
        this.nextStep.next(this.buildFormPayload());
      });

    if (this.isPendingPepFlow) {
      if (Array.isArray(this.initData?.airtableRepresentativeData) && this.initData.airtableRepresentativeData.length > 0) {
        this.populateFormFromAirtableRepresentativeData();
      } else {
        this.resetFormForPendingPepFlow();
      }
    } else if (this.initData) {
      this.populateForm();
    }
  }

  onContinue(): void {
    this.formSubmitSubject$.next();
  }

  get isPEP(): boolean {
    return this.pepDataForm.get('isPEP')?.value;
  }

  get hasPEPRelatives(): boolean {
    return this.pepDataForm.get('hasPEPRelatives')?.value;
  }

  get pepList(): FormArray {
    return this.pepDataForm.get('pepList') as FormArray;
  }

  get familyPepList(): FormArray {
    return this.pepDataForm.get('familyPepList') as FormArray;
  }

  getfamily2ndDegreeMembers(index: number): FormArray {
    return this.pepList.at(index).get('family2ndDegreeMembers') as FormArray;
  }

  getFamilyMembers(index: number): FormArray {
    return this.familyPepList.at(index).get('familyMembers') as FormArray;
  }

  addPep(): void {
    this.pepList.push(this.createPep());
  }

  removePep(index: number): void {
    this.pepList.removeAt(index);
  }

  add2ndDegreeMember(index: number): void {
    this.getfamily2ndDegreeMembers(index).push(this.createFamilyPepMember());
  }

  addFamilyPep(): void {
    this.familyPepList.push(this.createFamilyPep());
  }

  removeFamilyPep(index: number): void {
    this.familyPepList.removeAt(index);
  }

  addFamilyMember(index: number): void {
    this.getFamilyMembers(index).push(this.createFamilyPepMember());
  }

  private createPep() {
    const formGroup = this.formBuilder.group({
      peplegalRepresentative: ['', [Validators.required]],
      hasBeenWorkingOnPublicInstitution: [false, [Validators.required]],
      partnerName: ['', []],
      family2ndDegreeMembers: this.formBuilder.array([this.createFamilyPepMember(true)]),
    });

    formGroup.get('hasBeenWorkingOnPublicInstitution').valueChanges.subscribe(value => {
      if (value) {
        formGroup.addControl('positionPartner', this.formBuilder.control('', Validators.required));
        formGroup.addControl('institutionName', this.formBuilder.control('', Validators.required));
        formGroup.updateValueAndValidity();
      } else {
        formGroup.removeControl('positionPartner');
        formGroup.removeControl('institutionName');
      }
    });

    formGroup.get('peplegalRepresentative').valueChanges.subscribe(value => {
      this.validateDuplicatePep(formGroup, value, 'peplegalRepresentative', 'pepList');
    });

    if (this.isPendingPepFlow && this.hasSingleAirtableRepresentative) {
      const defaultValue = this.airtableRepresentativesList[0]?.value;
      if (defaultValue) {
        formGroup.get('peplegalRepresentative')?.setValue(defaultValue);
      }
    }

    return formGroup;
  }

  private createFamilyPep(): FormGroup {
    const formGroup = this.formBuilder.group({
      pepRelativeslegalRepresentative: ['', [Validators.required]],
      familyMembers: this.formBuilder.array([this.createFamilyPepMember(true)]),
    });

    formGroup.get('pepRelativeslegalRepresentative').valueChanges.subscribe(value => {
      this.validateDuplicatePep(formGroup, value, 'pepRelativeslegalRepresentative', 'familyPepList');
    });

    if (this.isPendingPepFlow && this.hasSingleAirtableRepresentative) {
      const defaultValue = this.airtableRepresentativesList[0]?.value;
      if (defaultValue) {
        formGroup.get('pepRelativeslegalRepresentative')?.setValue(defaultValue);
      }
    }

    return formGroup;
  }

  private createFamilyPepMember(required=false): FormGroup {
    return this.formBuilder.group({
      fullName: ['', required ? [Validators.required] : []],
      relationship: ['', required ? [Validators.required] : []],
    });
  }

  private resetFormForPendingPepFlow(): void {
    this.pepDataForm.patchValue({
      isPEP: false,
      hasPEPRelatives: false,
    });

    this.pepList.clear();
    this.familyPepList.clear();
  }

  private populateFormFromAirtableRepresentativeData(): void {
    const reps = this.initData?.airtableRepresentativeData || [];
    const hasAnyPep = reps.some((r: any) => !!r?.isPEP);
    const hasAnyRelatives = reps.some((r: any) => !!r?.hasPEPRelatives);

    this.pepDataForm.patchValue({
      isPEP: hasAnyPep,
      hasPEPRelatives: hasAnyRelatives,
    });

    this.pepList.clear();
    this.familyPepList.clear();

    reps.filter((r: any) => !!r?.isPEP).forEach((rep: any) => {
      const pepGroup = this.createPep();
      const value = `airtable_rep_${(rep?.documentNumber || '').trim()}`;
      pepGroup.patchValue({
        peplegalRepresentative: value,
        hasBeenWorkingOnPublicInstitution: !!rep?.hasBeenWorkingOnPublicInstitution,
        partnerName: rep?.partnerName ?? '',
      });
      if (rep?.hasBeenWorkingOnPublicInstitution) {
        pepGroup.patchValue({
          positionPartner: rep?.positionPartner ?? '',
          institutionName: rep?.institutionName ?? '',
        });
      }
      this.populateFamilyMembers(
        pepGroup.get('family2ndDegreeMembers') as FormArray,
        Array.isArray(rep?.family2ndDegreeMembers) ? rep.family2ndDegreeMembers : []
      );
      this.pepList.push(pepGroup);
    });

    reps.filter((r: any) => !!r?.hasPEPRelatives).forEach((rep: any) => {
      const familyGroup = this.createFamilyPep();
      const value = `airtable_rep_${(rep?.documentNumber || '').trim()}`;
      familyGroup.patchValue({
        pepRelativeslegalRepresentative: value,
      });
      this.populateFamilyMembers(
        familyGroup.get('familyMembers') as FormArray,
        Array.isArray(rep?.familyMembers) ? rep.familyMembers : []
      );
      this.familyPepList.push(familyGroup);
    });
  }

  private populateForm(): void {
    this.pepDataForm.patchValue({
      isPEP: this.initData?.isPEP ?? false,
      hasPEPRelatives: this.initData?.hasPEPRelatives ?? false,
    });

    if (this.initData?.pepList?.length) {
      this.pepList?.clear();

      this.initData.pepList.forEach(pep => {
        const pepGroup = this.createPep();

        pepGroup.patchValue({
          peplegalRepresentative: pep.peplegalRepresentative ?? '',
          hasBeenWorkingOnPublicInstitution: pep.hasBeenWorkingOnPublicInstitution ?? false,
          partnerName: pep.partnerName ?? '',
        });

        if (pep.hasBeenWorkingOnPublicInstitution) {
          pepGroup.patchValue({
            positionPartner: pep.positionPartner ?? '',
            institutionName: pep.institutionName ?? '',
          });
        }

        this.populateFamilyMembers(
          pepGroup.get('family2ndDegreeMembers') as FormArray,
          pep.family2ndDegreeMembers ?? []
        );

        this.pepList.push(pepGroup);
      });
    }

    if (this.initData?.familyPepList?.length) {
      this.familyPepList?.clear();

      this.initData.familyPepList.forEach(pep => {
        const pepGroup = this.createFamilyPep();

        pepGroup.patchValue({
          pepRelativeslegalRepresentative: pep.pepRelativeslegalRepresentative ?? '',
        });

        this.populateFamilyMembers(pepGroup.get('familyMembers') as FormArray, pep.familyMembers ?? []);

        this.familyPepList.push(pepGroup);
      });
    }
  }

  private validateDuplicatePep(currentGroup: FormGroup, value: any, controlName: string, listName:string) {
    if (!value) return;

    const duplicates = this[listName].controls.filter(
      ctrl => ctrl !== currentGroup && ctrl.get(controlName)?.value === value
    );

    const control = currentGroup.get(controlName);

    if (duplicates.length > 0) {
      control?.setErrors({ duplicated: true });
    } else {
      if (control?.hasError('duplicated')) {
        const errors = { ...control.errors };
        delete errors['duplicated'];

        control.setErrors(Object.keys(errors).length ? errors : null);
      }
    }
  }

  private populateFamilyMembers(familyArray: FormArray, membersData: any[]): void {
    familyArray?.clear();

    membersData.forEach(member => {
      familyArray.push(
        this.formBuilder.group({
          fullName: [member.fullName ?? '', Validators.required],
          relationship: [member.relationship ?? '', Validators.required],
        })
      );
    });
  }

  private buildFormPayload(): any {
    const rawValue = this.pepDataForm.value || {};
    const isPEP = !!rawValue.isPEP;
    const hasPEPRelatives = !!rawValue.hasPEPRelatives;
    const payload: any = {
      isPEP,
      hasPEPRelatives,
      pepList: [],
      familyPepList: [],
    };

    if (isPEP && Array.isArray(rawValue.pepList) && rawValue.pepList.length > 0) {
      const sanitizedPepList = rawValue.pepList
        .map((pep: any) => {
          if (!pep?.peplegalRepresentative) {
            return null;
          }

          const sanitized: any = {
            peplegalRepresentative: pep.peplegalRepresentative,
            hasBeenWorkingOnPublicInstitution: !!pep.hasBeenWorkingOnPublicInstitution,
          };

          if (pep.partnerName?.trim()) {
            sanitized.partnerName = pep.partnerName.trim();
          }

          if (sanitized.hasBeenWorkingOnPublicInstitution) {
            if (pep.positionPartner?.trim()) {
              sanitized.positionPartner = pep.positionPartner.trim();
            }
            if (pep.institutionName?.trim()) {
              sanitized.institutionName = pep.institutionName.trim();
            }
          }

          if (Array.isArray(pep.family2ndDegreeMembers)) {
            sanitized.family2ndDegreeMembers = pep.family2ndDegreeMembers
              .filter(
                (member: any) =>
                  member?.fullName?.trim() && member?.relationship?.trim()
              )
              .map((member: any) => ({
                fullName: member.fullName.trim(),
                relationship: member.relationship.trim(),
              }));
          }

          if (!sanitized.family2ndDegreeMembers) {
            sanitized.family2ndDegreeMembers = [];
          }

          return sanitized;
        })
        .filter(Boolean);
      
      if (sanitizedPepList.length > 0) {
        payload.pepList = sanitizedPepList;
      }
    }

    if (hasPEPRelatives && Array.isArray(rawValue.familyPepList) && rawValue.familyPepList.length > 0) {
      const sanitizedFamilyPepList = rawValue.familyPepList
        .map((pep: any) => {
          if (!pep?.pepRelativeslegalRepresentative) {
            return null;
          }

          const sanitized: any = {
            pepRelativeslegalRepresentative: pep.pepRelativeslegalRepresentative,
          };

          if (Array.isArray(pep.familyMembers)) {
            sanitized.familyMembers = pep.familyMembers
              .filter(
                (member: any) =>
                  member?.fullName?.trim() && member?.relationship?.trim()
              )
              .map((member: any) => ({
                fullName: member.fullName.trim(),
                relationship: member.relationship.trim(),
              }));
          }

          if (!sanitized.familyMembers) {
            sanitized.familyMembers = [];
          }

          return sanitized;
        })
        .filter(Boolean);
      
      // Only add familyPepList to payload if it has items
      if (sanitizedFamilyPepList.length > 0) {
        payload.familyPepList = sanitizedFamilyPepList;
      }
    }

    if (this.isPendingPepFlow) {
      payload.isPendingPepFlow = true;
      payload.pendingPepFlowMetadata = this.buildPendingPepMetadata(payload);
    }

    return payload;
  }

  private buildPendingPepMetadata(payload: any) {
    const pepMap = new Map<string, any>();
    (payload.pepList || []).forEach((pep: any) => {
      if (pep?.peplegalRepresentative) {
        pepMap.set(pep.peplegalRepresentative, pep);
      }
    });

    const relativesMap = new Map<string, any>();
    (payload.familyPepList || []).forEach((pep: any) => {
      if (pep?.pepRelativeslegalRepresentative) {
        relativesMap.set(pep.pepRelativeslegalRepresentative, pep);
      }
    });

    const representatives = this.airtableRepresentativesList.map(rep => ({
      value: rep.value,
      documentNumber: rep.documentNumber,
      names: rep.names,
      lastNames: rep.lastNames,
      pepData: pepMap.get(rep.value) || null,
      relativesData: relativesMap.get(rep.value) || null,
    }));

    return {
      globalAnswers: {
        isPEP: payload.isPEP,
        hasPEPRelatives: payload.hasPEPRelatives,
      },
      representatives,
    };
  }
}
